import {
  DecisionState,
  HomeCalendarDay,
  HomeCalendarEntry,
  HomeCalendarGranularity,
  HomeCalendarMonthSummary
} from "../../../models/home";
import { CalendarDateOperations } from "./calendar-date.operations";

export class CalendarPeriodBuilder {
  static buildWeekDayDates(anchorDate: Date): readonly Date[] {
    const start = CalendarPeriodBuilder.startOfWeek(anchorDate);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  }

  static buildDays(
    granularity: HomeCalendarGranularity,
    anchorDate: Date,
    entriesByDay: Map<string, readonly HomeCalendarEntry[]>,
    todayKey: string,
    selectedDayKey: string | null
  ): readonly HomeCalendarDay[] {
    if (granularity === "week") {
      const start = CalendarPeriodBuilder.startOfWeek(anchorDate);
      return Array.from({ length: 7 }, (_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return CalendarPeriodBuilder.buildDay(
          date,
          anchorDate,
          entriesByDay,
          todayKey,
          selectedDayKey,
          true,
          4
        );
      });
    }

    const start = CalendarPeriodBuilder.startOfGridMonth(anchorDate);
    const end = CalendarPeriodBuilder.endOfGridMonth(anchorDate);
    const days: HomeCalendarDay[] = [];
    const cursor = new Date(start);

    while (cursor <= end) {
      days.push(
        CalendarPeriodBuilder.buildDay(
          new Date(cursor),
          anchorDate,
          entriesByDay,
          todayKey,
          selectedDayKey,
          false,
          3
        )
      );
      cursor.setDate(cursor.getDate() + 1);
    }

    return days;
  }

  static buildMonthSummaries(
    anchorDate: Date,
    entriesByDay: Map<string, readonly HomeCalendarEntry[]>
  ): readonly HomeCalendarMonthSummary[] {
    const year = anchorDate.getFullYear();

    return Array.from({ length: 12 }, (_, monthIndex) => {
      const monthDate = new Date(year, monthIndex, 1, 12, 0, 0, 0);
      const monthKey = CalendarDateOperations.toDateKey(monthDate);
      const monthPrefix = monthKey.slice(0, 7);
      const matchingDays = Array.from(entriesByDay.entries())
        .filter(([dayKey]) => dayKey.startsWith(monthPrefix))
        .map(([dayKey, entries]) => ({ dayKey, entries }));

      const byState: Partial<Record<DecisionState, number>> = {};
      matchingDays.forEach(({ entries }) => {
        entries.forEach((entry) => {
          byState[entry.state] = (byState[entry.state] ?? 0) + 1;
        });
      });

      return {
        monthKey,
        monthDate,
        totalEntries: matchingDays.reduce((total, day) => total + day.entries.length, 0),
        byState,
        previewDays: matchingDays
          .sort((left, right) => {
            const countDelta = right.entries.length - left.entries.length;
            return countDelta !== 0 ? countDelta : left.dayKey.localeCompare(right.dayKey);
          })
          .slice(0, 3)
          .map((day) => ({
            dayKey: day.dayKey,
            dayOfMonth: CalendarDateOperations.parseDateKey(day.dayKey).getDate(),
            totalEntries: day.entries.length
          }))
      };
    });
  }

  private static buildDay(
    date: Date,
    anchorDate: Date,
    entriesByDay: Map<string, readonly HomeCalendarEntry[]>,
    todayKey: string,
    selectedDayKey: string | null,
    forceCurrentPeriod: boolean,
    visibleEntryLimit: number
  ): HomeCalendarDay {
    const dayKey = CalendarDateOperations.toDateKey(date);
    const entries = entriesByDay.get(dayKey) ?? [];
    const visibleEntries = entries.slice(0, visibleEntryLimit);

    return {
      dayKey,
      date,
      inCurrentPeriod: forceCurrentPeriod || CalendarPeriodBuilder.isSameMonth(date, anchorDate),
      isToday: dayKey === todayKey,
      isSelected: dayKey === selectedDayKey,
      entries,
      visibleEntries,
      overflowCount: Math.max(0, entries.length - visibleEntries.length)
    };
  }

  private static startOfWeek(anchorDate: Date): Date {
    const date = new Date(anchorDate);
    const offset = (date.getDay() + 6) % 7;
    date.setDate(date.getDate() - offset);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  }

  private static startOfGridMonth(anchorDate: Date): Date {
    const firstOfMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1, 12, 0, 0, 0);
    const offset = (firstOfMonth.getDay() + 6) % 7;
    firstOfMonth.setDate(firstOfMonth.getDate() - offset);
    return firstOfMonth;
  }

  private static endOfGridMonth(anchorDate: Date): Date {
    const lastOfMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 0, 12, 0, 0, 0);
    const offset = 6 - ((lastOfMonth.getDay() + 6) % 7);
    lastOfMonth.setDate(lastOfMonth.getDate() + offset);
    return lastOfMonth;
  }

  private static isSameMonth(left: Date, right: Date): boolean {
    return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();
  }
}
